import React, { useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Tooltip component that wraps children and shows a tooltip on hover.
 *
 * Usage:
 * <Tooltip content="This is the tooltip text">
 *   <Button disabled>Hover me</Button>
 * </Tooltip>
 */
const Tooltip = ({ children, content, position = 'top', variant = 'dark', className }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!content) {
    return children;
  }

  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  // Bubble color per variant. `light` matches the muted-grey design used for
  // status hints (e.g. the Medical Records icon); `dark` is the default.
  const bubbleVariants = {
    dark: 'text-white bg-gray-900',
    light: 'text-gray-800 bg-gray-200',
  };

  // Arrow placement (which side points at the trigger). Color is applied
  // separately per variant so the arrow always matches the bubble.
  const arrowPositions = {
    top: 'top-full left-1/2 -translate-x-1/2 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-y-transparent border-l-transparent',
  };

  const arrowColors = {
    dark: {
      top: 'border-t-gray-900',
      bottom: 'border-b-gray-900',
      left: 'border-l-gray-900',
      right: 'border-r-gray-900',
    },
    light: {
      top: 'border-t-gray-200',
      bottom: 'border-b-gray-200',
      left: 'border-l-gray-200',
      right: 'border-r-gray-200',
    },
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Wrapper to capture hover on disabled elements */}
      <div className="contents">
        {children}
      </div>

      {isVisible && (
        <div
          className={cn(
            'absolute z-50 px-3 py-2 text-sm rounded-md shadow-lg whitespace-nowrap',
            bubbleVariants[variant],
            'animate-in fade-in-0 zoom-in-95 duration-100',
            positions[position],
            className
          )}
          role="tooltip"
        >
          {content}
          <div
            className={cn(
              'absolute w-0 h-0 border-4',
              arrowPositions[position],
              arrowColors[variant][position]
            )}
          />
        </div>
      )}
    </div>
  );
};

export { Tooltip };
